import {
  forgetRouteLimits,
  randomUuid,
  useFeatureGate,
} from '@funkit/connect-core'
import { useQueryClient } from '@tanstack/react-query'
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type LayoutChangeEvent, View, type ViewStyle } from 'react-native'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import {
  type ClipboardReader,
  type ClipboardWriter,
  copyToClipboard as defaultCopyToClipboard,
  pasteFromClipboard as defaultPasteFromClipboard,
  guardClipboardReader,
} from '../transfer/clipboard'
import { NativeCapabilitiesProvider } from '../providers/NativeCapabilitiesContext'
import { FlowContent } from './DepositFlowContent'
import type {
  DepositClientMetadata,
  DepositFlowCallbacks,
  DepositFlowConfig,
} from './depositFlowConfig'
import {
  hasFiatMethod,
  resolveDepositMethods,
  resolveFiatRail,
  resolveInitialDepositCheckoutConfig,
} from './depositMethods'
import { FunkitMisconfiguredError } from './flowError'
import { FlowDismissLockProvider } from './FlowDismissLock'
import { type BackdropBlurRenderer, FlowSheet } from './FlowSheet'
import { FlowContainerHeightProvider } from './flowStepHeight'
import { guardHostCallbacks } from './guardHostCallbacks'
import { isLockedDeposit } from './isLockedDeposit'
import { useCopiedToast } from './useCopiedToast'
import { useDepositReceivedToast } from './useDepositReceivedToast'
import { validateClientMetadata } from './validateClientMetadata'
import { validateInitialAmount } from './validateInitialAmount'
import { WithdrawalFlowContent } from './WithdrawalFlowContent'
import type {
  WithdrawalFlowCallbacks,
  WithdrawalFlowConfig,
} from './withdrawalFlowConfig'
import type { StripeOnrampClient } from '../fiat/providers/stripeOnramp'
import { StripeOnrampBridge } from '../fiat/providers/stripeOnrampBridge'
import type { StripeOnrampConfig } from '../fiat/providers/useStripeOnrampClient'

export type FlowScreen =
  | 'depositAmount'
  | 'addMoney'
  | 'transfer'
  | 'swapped'
  | 'withdrawMethod'
  | 'withdrawAmount'
  | 'withdrawAddress'
  | 'withdrawReceiveToken'
  | 'withdrawComplete'

/**
 * The open flow's mode + its config/callbacks, as a discriminated union so
 * deposit and withdrawal state can't cross-contaminate (a `beginDeposit` after
 * a withdrawal fully replaces the slot, and vice versa).
 */
type ActiveFlow =
  | {
      mode: 'deposit'
      checkoutId: string
      clientMetadata: DepositClientMetadata | undefined
      config: DepositFlowConfig
      callbacks: DepositFlowCallbacks
    }
  | {
      mode: 'withdrawal'
      checkoutId: string
      config: WithdrawalFlowConfig
      callbacks: WithdrawalFlowCallbacks
    }

interface FlowState {
  isOpen: boolean
  stack: FlowScreen[]
  flow: ActiveFlow | null
  /**
   * Bumped on every `begin*`. The sheet keys its present on this, so a new
   * session opened while a previous one is mid-dismiss re-presents (cancelling
   * the stale dismiss) instead of letting the late `onDismiss` close it.
   */
  session: number
}

const CRYPTO_WITHDRAWAL_STEPS = new Set<FlowScreen>([
  'withdrawAmount',
  'withdrawAddress',
  'withdrawReceiveToken',
  'withdrawComplete',
])

function shouldUseFullHeight(
  current: FlowScreen | undefined,
  flow: ActiveFlow | null,
): boolean {
  if (!current) {
    return false
  }
  if (CRYPTO_WITHDRAWAL_STEPS.has(current) || current === 'depositAmount') {
    return true
  }
  return (
    current === 'transfer' &&
    flow?.mode === 'deposit' &&
    isLockedDeposit(flow.config)
  )
}

const CLOSED: FlowState = {
  isOpen: false,
  stack: [],
  flow: null,
  session: 0,
}

// The V2 deposit experience ships dark behind one program-level gate: V2
// surfaces open only when the host configures them AND the gate is on (off in
// prod; enabled per customer/test cohort in the Statsig console). Off — or
// before Statsig has initialized, when every gate reads false —
// `beginDeposit` opens the unchanged V1 Add money flow.
const RN_V2_DEPOSIT_GATE = 'enable-rn-v2-deposit'

interface FunkitFlowContextValue {
  /**
   * Open the managed deposit flow. Resolves once the sheet is presenting. A
   * config that cannot open throws {@link FunkitMisconfiguredError} before any
   * promise exists, so `await beginDeposit()` and a bare call both see it at
   * the tap.
   */
  beginDeposit: (
    config: DepositFlowConfig,
    callbacks?: DepositFlowCallbacks,
  ) => Promise<void>
  /** Open the managed withdrawal flow at the payout-method screen. */
  beginWithdrawal: (
    config: WithdrawalFlowConfig,
    callbacks?: WithdrawalFlowCallbacks,
  ) => void
  /** Close the flow. */
  closeFlow: () => void
}

const FunkitFlowContext = createContext<FunkitFlowContextValue | null>(null)

export interface FlowNav {
  navigate: (s: FlowScreen) => void
  back: () => void
  /** Replace the whole stack with a single screen (never closes the sheet). */
  reset: (s: FlowScreen) => void
  /**
   * Pop back to `s`, however many screens deep it is — what `back` can't do once
   * a step lands between two that navigate to each other. A no-op when `s` isn't
   * on the stack.
   */
  popTo: (s: FlowScreen) => void
  closeFlow: () => void
}

/**
 * Mounts the managed deposit-flow modal and exposes `beginDeposit` to
 * {@link useFunkitCheckout}. The five screens are rendered + navigated here
 * (host apps never render them directly), mirroring how `@funkit/connect`'s
 * `FunkitProvider` owns the checkout modal.
 */
export function FunkitFlowProvider({
  children,
  copyToClipboard = defaultCopyToClipboard,
  pasteFromClipboard = defaultPasteFromClipboard,
  renderBackdropBlur,
  liquidGlass,
  stripeOnramp,
  onOpenChange,
}: {
  children: ReactNode
  /** Host clipboard override; defaults to the bare-RN clipboard peer. */
  copyToClipboard?: ClipboardWriter
  /** Host clipboard-read override; defaults to the bare-RN clipboard peer. */
  pasteFromClipboard?: ClipboardReader
  /** Host replacement for the sheet material. See {@link BackdropBlurRenderer}. */
  renderBackdropBlur?: BackdropBlurRenderer
  /** Set with a replacement that is iOS 26 Liquid Glass rather than a blur. */
  liquidGlass?: boolean
  /** Stripe account config; the client is built from it here. */
  stripeOnramp?: StripeOnrampConfig
  /**
   * Fires on the closed→open and open→closed transitions, and again with
   * `true` when a `begin*` call replaces a flow that is already open.
   */
  onOpenChange?: (isOpen: boolean) => void
}) {
  const [state, setState] = useState<FlowState>(CLOSED)
  const closing = useRef(false)
  const pendingOpens = usePendingOpens(state, setState)
  // Counted here rather than in the updater so the same value can be published
  // before the flow renders. Published from the handlers, not an effect: a
  // replacement must mount with the identity re-read for it, not one render
  // late on the one before it.
  const lastSession = useRef(CLOSED.session)
  const openSession = () => {
    lastSession.current += 1
    closing.current = false
    onOpenChange?.(true)
    return lastSession.current
  }
  // Counted, not flagged: two screens may hold the sheet at once, and the
  // first to let go must not free it for the other.
  const [dismissLocks, setDismissLocks] = useState(0)
  const acquireDismissLock = useCallback(() => {
    setDismissLocks((held) => held + 1)
    return () => setDismissLocks((held) => Math.max(0, held - 1))
  }, [])
  const { hideCryptoDeposit, getIdentityAssertion } = useFunkitIdentity()
  const queryClient = useQueryClient()
  // Built here rather than by the host: a host hands in config and never an
  // implementation. `undefined` config means `undefined` client, and no Stripe
  // surface claims a state.
  //
  // Held as state rather than read from a hook: the SDK is an optional peer that
  // `StripeOnrampBridge` loads on demand, rendered below only when a host
  // configured Stripe, so it stays off this module's import graph.
  // A full-height step fills the box the flow is mounted in, which on web is
  // not always the window. Measured here because this view *is* that box.
  const [rootHeight, setRootHeight] = useState<number | undefined>(undefined)
  const onRootLayout = useCallback((event: LayoutChangeEvent) => {
    setRootHeight(event.nativeEvent.layout.height)
  }, [])
  const [stripeOnrampClient, setStripeOnrampClient] = useState<
    StripeOnrampClient | undefined
  >(undefined)
  const isV2DepositEnabled = useFeatureGate(RN_V2_DEPOSIT_GATE)

  // Memoised so a provider re-render doesn't hand every capability consumer a
  // fresh object. The read is guarded here for the same reason as the callbacks
  // below; the write isn't: its `void` return means success, so a swallowed
  // failure would read as one (see useCopyDepositAddress, which guards it where
  // the result is compared).
  const nativeCapabilities = useMemo(
    () => ({
      renderBackdropBlur,
      ...(liquidGlass === undefined ? {} : { liquidGlass }),
      copyToClipboard,
      pasteFromClipboard: guardClipboardReader(pasteFromClipboard),
      stripeOnramp: stripeOnrampClient,
    }),
    [
      renderBackdropBlur,
      liquidGlass,
      copyToClipboard,
      pasteFromClipboard,
      stripeOnrampClient,
    ],
  )

  // The two doors host callbacks come in through: guarded here, so everything
  // downstream reads them off flow state already wrapped.
  const beginDeposit = useCallback(
    (
      config: DepositFlowConfig,
      hostCallbacks: DepositFlowCallbacks = {},
    ): Promise<void> => {
      const methods = resolveDepositMethods(config)
      const sanitizedConfig: DepositFlowConfig = {
        ...config,
        ...validateInitialAmount(config),
      }
      // See {@link RN_V2_DEPOSIT_GATE}: host opt-in alone is not enough.
      const useAmountEntry = !!sanitizedConfig.amountEntry && isV2DepositEnabled
      const checkoutId = randomUuid()
      logger.setFlowContext({ traceId: checkoutId })
      const clientMetadata = validateClientMetadata(config.clientMetadata)
      if (useAmountEntry) {
        assertFiatRailConfigured(sanitizedConfig, getIdentityAssertion)
      } else if (
        hasFiatMethod(config) &&
        resolveFiatRail(
          config,
          resolveInitialDepositCheckoutConfig(sanitizedConfig),
        ) === undefined
      ) {
        logger.warn('connect-rn:deposit:fiat_method_without_target_asset', {
          checkoutId,
          methods,
        })
      }
      // Provider-level funding-option decisions ride along with the config.
      logger.info('beginDeposit', {
        ...sanitizedConfig,
        checkoutId,
        clientMetadata,
        hideCryptoDeposit,
        isV2DepositEnabled,
        methods,
        useAmountEntry,
      })
      const callbacks = guardHostCallbacks(hostCallbacks)
      const session = openSession()
      // What a route allows is remembered for as long as the sheet is up and no
      // longer. A window that rolled over, an allowance spent somewhere else, a
      // tier raised outside the app — none of those announce themselves, and
      // opening the sheet is the moment the buyer expects to be told where they
      // stand. So the session starts knowing nothing and learns from its own
      // first quote (ENG-6302).
      forgetRouteLimits(queryClient)
      callbacks.onOpen?.()
      return pendingOpens.open({
        isOpen: true,
        stack: [useAmountEntry ? 'depositAmount' : 'addMoney'],
        flow: {
          mode: 'deposit',
          checkoutId,
          clientMetadata,
          config: sanitizedConfig,
          callbacks,
        },
        session,
      })
    },
    [
      hideCryptoDeposit,
      getIdentityAssertion,
      isV2DepositEnabled,
      onOpenChange,
      pendingOpens,
      queryClient,
    ],
  )

  const beginWithdrawal = useCallback(
    (
      config: WithdrawalFlowConfig,
      hostCallbacks: WithdrawalFlowCallbacks = {},
    ) => {
      const checkoutId = randomUuid()
      logger.setFlowContext({ traceId: checkoutId })
      logger.info('beginWithdrawal', { ...config, checkoutId })
      const callbacks = guardHostCallbacks(hostCallbacks)
      const session = openSession()
      forgetRouteLimits(queryClient)
      callbacks.onOpen?.()
      setState({
        isOpen: true,
        stack: ['withdrawMethod'],
        flow: { mode: 'withdrawal', checkoutId, config, callbacks },
        session,
      })
    },
    [onOpenChange],
  )

  const { toast, showCopiedToast, clearToast } = useCopiedToast()
  // Not cleared on close: the flow closing is what raises it.
  const { depositToast, showDepositToast } = useDepositReceivedToast()

  const closeFlow = useCallback(() => {
    if (!state.isOpen || closing.current) {
      return
    }
    closing.current = true
    state.flow?.callbacks.onClose?.()
    setState(CLOSED)
    onOpenChange?.(false)
    clearToast()
    // The flush matters because the batch timer would otherwise strand
    // whatever the flow logged on its way out.
    logger.clearFlowContext()
    logger.flush()
  }, [clearToast, onOpenChange, state])

  const navigate = useCallback((screen: FlowScreen) => {
    setState((prev) => ({ ...prev, stack: [...prev.stack, screen] }))
  }, [])

  const reset = useCallback((screen: FlowScreen) => {
    setState((prev) => (prev.isOpen ? { ...prev, stack: [screen] } : prev))
  }, [])

  const popTo = useCallback((screen: FlowScreen) => {
    setState((prev) => {
      const index = prev.stack.lastIndexOf(screen)
      if (!prev.isOpen || index < 0) {
        return prev
      }
      return { ...prev, stack: prev.stack.slice(0, index + 1) }
    })
  }, [])

  const back = useCallback(() => {
    if (!state.isOpen) {
      return
    }
    if (state.stack.length <= 1) {
      closeFlow()
      return
    }
    setState((prev) => {
      if (!prev.isOpen) {
        return prev
      }
      return { ...prev, stack: prev.stack.slice(0, -1) }
    })
  }, [closeFlow, state])

  const value = useMemo<FunkitFlowContextValue>(
    () => ({ beginDeposit, beginWithdrawal, closeFlow }),
    [beginDeposit, beginWithdrawal, closeFlow],
  )

  const current = state.stack[state.stack.length - 1]
  // Each flow's home screen is its stack root (Add money / payout methods) —
  // they only ever appear at depth 0.
  const isHomeScreen = state.stack.length <= 1

  const nav = useMemo<FlowNav>(
    () => ({ navigate, back, reset, popTo, closeFlow }),
    [navigate, back, reset, popTo, closeFlow],
  )

  return (
    <FunkitFlowContext.Provider value={value}>
      {stripeOnramp === undefined ? null : (
        <StripeOnrampBridge
          config={stripeOnramp}
          onClient={setStripeOnrampClient}
        />
      )}
      <FlowDismissLockProvider value={acquireDismissLock}>
        {/* Above the root view, so the toasts below — which outlive the sheet —
          can read the host's blur and draw themselves as glass. */}
        <NativeCapabilitiesProvider capabilities={nativeCapabilities}>
          <View style={rootContainer} onLayout={onRootLayout}>
            <FlowContainerHeightProvider height={rootHeight}>
              {/* FlowSheet wraps the host app (children) so the gorhom path's portal
              host spans the full screen; the flow screens render as `content`. */}
              <FlowSheet
                isOpen={state.isOpen}
                session={state.session}
                onClose={closeFlow}
                // The Swapped embed focuses an input inside its WebView, which gorhom
                // can't see; the rail's own screens are a full window and pad their own
                // footer. See useKeyboardHeight.
                keyboardAvoidEnabled={current === 'swapped'}
                // Transfer opts in (swiping it exits the flow); other pushed screens —
                // especially the Swapped embed mid-payment — must not be dismissable.
                // The outcome opts in too: the funds have already moved, and while it
                // settles the design's button is a spinner, so a swipe is the only way
                // out of a poll that never comes back.
                enableSwipeToDismiss={
                  isHomeScreen ||
                  current === 'transfer' ||
                  current === 'withdrawComplete'
                }
                dismissLocked={dismissLocks > 0}
                // See {@link CRYPTO_WITHDRAWAL_STEPS}. The V2 amount surface is the
                // whole screen by design.
                fullHeight={shouldUseFullHeight(current, state.flow)}
                glass={current === 'depositAmount'}
                content={
                  // Mounted only while open so the flow queries run only then.
                  state.isOpen && state.flow ? (
                    state.flow.mode === 'deposit' ? (
                      <FlowContent
                        key={state.flow.checkoutId}
                        checkoutId={state.flow.checkoutId}
                        clientMetadata={state.flow.clientMetadata}
                        config={state.flow.config}
                        current={current}
                        nav={nav}
                        onCopySuccess={showCopiedToast}
                        showDepositToast={showDepositToast}
                        callbacks={state.flow.callbacks}
                      />
                    ) : (
                      <WithdrawalFlowContent
                        checkoutId={state.flow.checkoutId}
                        config={state.flow.config}
                        callbacks={state.flow.callbacks}
                        current={current}
                        nav={nav}
                      />
                    )
                  ) : null
                }
              >
                {children}
              </FlowSheet>
              {/* Later siblings of FlowSheet: later siblings sit on top in RN, so
              the toasts float above the gorhom sheet without a <Modal> (which
              would block all touches for its entire lifetime). */}
              {toast}
              {depositToast}
            </FlowContainerHeightProvider>
          </View>
        </NativeCapabilitiesProvider>
      </FlowDismissLockProvider>
    </FunkitFlowContext.Provider>
  )
}

const rootContainer: ViewStyle = { flex: 1 }

/**
 * Lets `begin*` hand back a promise that settles once the session it opened
 * has committed, so a host awaiting the call is not told "open" a render
 * early. A session replaced, closed or unmounted before it presented settles
 * then too — resolved, not rejected: most hosts fire and forget, and an
 * unhandled rejection for a flow they themselves replaced helps no one.
 * Rejection is reserved for a config that cannot open at all.
 */
function usePendingOpens(
  state: FlowState,
  setState: Dispatch<SetStateAction<FlowState>>,
): {
  /** Sets the open state; resolves once that session is committed. */
  open: (next: FlowState) => Promise<void>
} {
  const pending = useRef<Array<{ session: number; resolve: () => void }>>([])
  // Settles every session up to the committed one; a later session still
  // waiting on its own commit stays queued.
  const settle = (upTo: number) => {
    const [done, waiting] = pending.current.reduce<
      [typeof pending.current, typeof pending.current]
    >(
      ([d, w], p) => (p.session <= upTo ? [[...d, p], w] : [d, [...w, p]]),
      [[], []],
    )
    pending.current = waiting
    done.forEach((p) => p.resolve())
  }
  // A close resets the session counter, and leaves nothing to wait for.
  useEffect(() => {
    settle(state.isOpen ? state.session : Number.POSITIVE_INFINITY)
  }, [state])
  useEffect(() => () => settle(Number.POSITIVE_INFINITY), [])
  return useMemo(
    () => ({
      open: (next) =>
        new Promise<void>((resolve) => {
          pending.current.push({ session: next.session, resolve })
          setState(next)
        }),
    }),
    [setState],
  )
}

/**
 * A fiat rail that cannot run must not open: the host would compile a config
 * whose submit goes nowhere. Thrown, not logged and swallowed, because this is
 * the integration's mistake and the tap that hit it is the place to see it.
 */
function assertFiatRailConfigured(
  config: DepositFlowConfig,
  getIdentityAssertion: (() => Promise<string>) | undefined,
): void {
  if (!hasFiatMethod(config)) {
    return
  }
  const fail = (reason: string) => {
    logger.warn('connect-rn:deposit:misconfigured', {
      reason,
      methods: config.methods,
    })
    return new FunkitMisconfiguredError(reason)
  }
  if (getIdentityAssertion === undefined) {
    throw fail('getIdentityAssertion is required for a fiat deposit method')
  }
  const rail = resolveFiatRail(
    config,
    resolveInitialDepositCheckoutConfig(config),
  )
  if (rail === undefined) {
    throw fail(
      'a fiat target ticker and checkout target are required for a fiat deposit method',
    )
  }
  if (rail.paymentMethod === 'card') {
    throw fail('card deposits are not available yet')
  }
}

export function useFunkitFlow(): FunkitFlowContextValue {
  const value = useContext(FunkitFlowContext)
  if (value == null) {
    throw new Error('useFunkitFlow must be used within <FunkitProvider>')
  }
  return value
}
